import { doc, deleteDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase/config';

interface UsernameCheckResult {
  available: boolean;
  message: string;
  valid: boolean;
}

export class UsernameService {
  /**
   * Validates username format
   */
  static validateUsernameFormat(username: string): { valid: boolean; message: string } {
    if (!username) {
      return { valid: false, message: 'Username is required' };
    }

    if (username.length < 3) {
      return { valid: false, message: 'Username must be at least 3 characters' };
    }

    if (username.length > 20) {
      return { valid: false, message: 'Username must be 20 characters or less' };
    }

    const usernamePattern = /^[a-zA-Z0-9_]+$/;
    if (!usernamePattern.test(username)) {
      return { valid: false, message: 'Username can only contain letters, numbers, and underscores' };
    }

    // Additional check for reserved usernames
    // Matches RESERVED_USERNAMES in PeakPWA functions/src/usernames.ts.
    const reservedUsernames = ['admin', 'administrator', 'root', 'peak', 'peakapp', 'support', 'help', 'api', 'www', 'staff', 'moderator', 'official', 'system', 'security', 'team'];
    if (reservedUsernames.includes(username.toLowerCase())) {
      return { valid: false, message: 'This username is reserved' };
    }

    return { valid: true, message: 'Username format is valid' };
  }

  /**
   * Checks if username is available (not taken by another user)
   */
  static async checkUsernameAvailability(username: string): Promise<UsernameCheckResult> {
    try {
      // First validate format
      const formatCheck = this.validateUsernameFormat(username);
      if (!formatCheck.valid) {
        return {
          available: false,
          message: formatCheck.message,
          valid: false
        };
      }

      // Personal Information is after the account exists. A signed-in client
      // may read usernames/{id}. The public callable only checks format, so
      // it always reports a well-formed name as free.
      const uid = auth.currentUser?.uid;
      if (uid) {
        const snap = await getDoc(doc(db, 'usernames', username.toLowerCase()));
        if (snap.exists() && snap.data()?.userId !== uid) {
          return {
            available: false,
            message: 'Username is already taken',
            valid: true,
          };
        }
        return {
          available: true,
          valid: true,
          message: 'Username is available',
        };
      }

      const check = httpsCallable<{ username: string }, UsernameCheckResult>(functions, 'checkUsernameAvailable');
      const { data } = await check({ username });
      return data;
    } catch (error) {
      console.error('Error checking username availability:', error);
      return {
        available: false,
        message: 'Unable to check username availability. Please try again.',
        valid: true
      };
    }
  }

  /**
   * Releases a username doc so it can be claimed by someone else.
   * Only the owning uid can do this — enforced by firestore.rules.
   */
  static async releaseUsername(username: string): Promise<void> {
    await deleteDoc(doc(db, 'usernames', username.toLowerCase()));
  }

  /**
   * Debounced username check function
   */
  static debounceUsernameCheck = (
    callback: (result: UsernameCheckResult) => void,
    delay: number = 500
  ) => {
    let timeoutId: NodeJS.Timeout;
    
    return (username: string) => {
      clearTimeout(timeoutId);
      
      if (!username.trim()) {
        callback({
          available: false,
          message: '',
          valid: false
        });
        return;
      }

      timeoutId = setTimeout(async () => {
        const result = await this.checkUsernameAvailability(username);
        callback(result);
      }, delay);
    };
  };
}

export const usernameService = UsernameService; 